import React, { useState, useEffect, useRef } from 'react';
import { 
    Mail, Plus, List, Send, Eye, RefreshCw, BarChart2, CheckCircle2, 
    XCircle, Clock, Info, ChevronRight, AlertCircle, Sparkles, Sliders, Download
} from 'lucide-react';
import io from 'socket.io-client';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';

export default function ManageCampaigns() {
    const toast = useToast();
    const [campaigns, setCampaigns] = useState([]);
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [view, setView] = useState('list'); // 'list', 'create', 'details'
    const [loading, setLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [sending, setSending] = useState(false);
    const [socket, setSocket] = useState(null);

    // Form states
    const [name, setName] = useState('');
    const [subject, setSubject] = useState('');
    const [contentType, setContentType] = useState('html'); // 'text' | 'html'
    const [body, setBody] = useState('');
    const [sendType, setSendType] = useState('normal'); // 'normal' | 'batch'
    const [batchSize, setBatchSize] = useState(10);
    const [batchDelaySeconds, setBatchDelaySeconds] = useState(5);

    // Stats states
    const [selectedStats, setSelectedStats] = useState(null);

    // Fetch campaigns
    const fetchCampaigns = async () => {
        setLoading(true);
        try {
            const res = await api.get('/api/campaigns');
            setCampaigns(res.data);
        } catch (err) {
            console.error('Error fetching campaigns:', err);
            toast.error('Failed to load campaigns');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCampaigns();

        // Connect to Socket.io for live updates
        const token = localStorage.getItem('token');
        const socketInstance = io(window.location.origin.replace('3000', '5000').replace('5173', '5001'), {
            auth: { token }
        });

        socketInstance.on('connect', () => {
            console.log('[SOCKET] Connected to campaigns gateway');
        });

        socketInstance.on('campaignStatsUpdate', (data) => {
            console.log('[SOCKET] Stats update received:', data);
            
            // Update campaigns list if present
            setCampaigns(prev => prev.map(c => {
                if (c.id === data.campaignId) {
                    return { ...c, ...data };
                }
                return c;
            }));

            // Update details view if selected
            setSelectedCampaign(prev => {
                if (prev && prev.campaign.id === data.campaignId) {
                    return {
                        ...prev,
                        campaign: { ...prev.campaign, ...data }
                    };
                }
                return prev;
            });
        });

        setSocket(socketInstance);

        return () => {
            socketInstance.disconnect();
        };
    }, []);

    const handleCreate = async (e) => {
        e.preventDefault();
        setCreating(true);
        try {
            const payload = {
                name,
                subject,
                contentType,
                body,
                sendType,
                batchSize: parseInt(batchSize),
                batchDelaySeconds: parseInt(batchDelaySeconds)
            };
            const res = await api.post('/api/campaigns', payload);
            toast.success('Campaign created as draft!');
            fetchCampaigns();
            
            // Reset form
            setName('');
            setSubject('');
            setBody('');
            setView('list');
        } catch (err) {
            console.error('Error creating campaign:', err);
            toast.error(err.response?.data?.message || 'Failed to create campaign');
        } finally {
            setCreating(false);
        }
    };

    const handleSend = async (campaignId) => {
        setSending(true);
        try {
            const res = await api.post(`/api/campaigns/${campaignId}/send`);
            toast.success(res.data.message || 'Campaign sending started!');
            fetchCampaigns();
            if (view === 'details') {
                viewCampaignDetails(campaignId);
            }
        } catch (err) {
            console.error('Error sending campaign:', err);
            toast.error('Failed to send campaign');
        } finally {
            setSending(false);
        }
    };

    const handleExportCSV = () => {
        if (!selectedCampaign || !selectedCampaign.recipients) return;
        
        const headers = ['Email', 'Status', 'Sent At', 'Opened At', 'Bounce Reason'];
        const rows = selectedCampaign.recipients.map(rec => [
            rec.email,
            rec.status,
            rec.sent_at ? new Date(rec.sent_at).toLocaleString() : '',
            rec.opened_at ? new Date(rec.opened_at).toLocaleString() : '',
            rec.bounce_reason || ''
        ]);

        const csvContent = [
            headers.join(','),
            ...rows.map(e => e.map(val => `"${String(val).replace(/"/g, '""')}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `campaign_${selectedCampaign.campaign.name.replace(/\s+/g, '_')}_stats.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('Campaign statistics exported successfully!');
    };

    const viewCampaignDetails = async (campaignId) => {
        setLoading(true);
        try {
            const res = await api.get(`/api/campaigns/${campaignId}`);
            setSelectedCampaign(res.data);
            setView('details');
        } catch (err) {
            console.error('Error fetching campaign details:', err);
            toast.error('Failed to load campaign statistics');
        } finally {
            setLoading(false);
        }
    };

    // Render Live Preview
    const renderPreview = () => {
        const previewHtml = contentType === 'html' ? body : `<div style="white-space: pre-wrap; font-family: sans-serif; padding: 20px;">${body}</div>`;
        return (
            <div className="border border-slate-200 rounded-2xl overflow-hidden bg-white shadow-inner h-[400px]">
                <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 text-xs text-slate-400 font-mono flex items-center space-x-2">
                    <span className="w-3 h-3 rounded-full bg-red-400"></span>
                    <span className="w-3 h-3 rounded-full bg-yellow-400"></span>
                    <span className="w-3 h-3 rounded-full bg-green-400"></span>
                    <span className="ml-2 font-semibold text-slate-600">Email Preview - Subject: {subject || '(No Subject)'}</span>
                </div>
                <iframe 
                    title="Live Preview" 
                    srcDoc={previewHtml || '<p style="color: #94a3b8; text-align: center; padding-top: 50px;">Enter some body text to see preview...</p>'} 
                    className="w-full h-[calc(100%-36px)] border-none"
                />
            </div>
        );
    };

    return (
        <div className="space-y-6">
            {/* Header Area */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
                <div>
                    <h1 className="text-3xl font-black text-gray-900 flex items-center">
                        <Mail className="mr-3 text-primary-500 w-8 h-8" /> Email Campaigns
                    </h1>
                    <p className="text-gray-400 text-sm mt-1">Manage, dispatch, and track custom newsletters to your customers.</p>
                </div>
                <div className="flex items-center gap-2">
                    {view !== 'list' && (
                        <button 
                            onClick={() => { setView('list'); fetchCampaigns(); }} 
                            className="btn-secondary px-5 py-3 rounded-xl text-xs uppercase font-black tracking-wider flex items-center gap-1.5"
                        >
                            <List className="w-4 h-4" /> Back to List
                        </button>
                    )}
                    {view === 'list' && (
                        <button 
                            onClick={() => setView('create')} 
                            className="btn-primary px-5 py-3 rounded-xl text-xs uppercase font-black tracking-wider flex items-center gap-1.5 shadow-lg shadow-primary-200"
                        >
                            <Plus className="w-4 h-4" /> New Campaign
                        </button>
                    )}
                </div>
            </div>

            {/* List View */}
            {view === 'list' && (
                <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
                    {loading && campaigns.length === 0 ? (
                        <div className="p-12 text-center text-gray-400">
                            <RefreshCw className="animate-spin w-8 h-8 mx-auto mb-2 text-primary-500" />
                            <span>Loading campaigns...</span>
                        </div>
                    ) : campaigns.length === 0 ? (
                        <div className="p-16 text-center text-gray-400 space-y-4">
                            <div className="w-16 h-16 bg-primary-50 text-primary-500 rounded-2xl flex items-center justify-center mx-auto">
                                <Mail className="w-8 h-8" />
                            </div>
                            <h3 className="text-lg font-bold text-gray-800">No campaigns found</h3>
                            <p className="max-w-md mx-auto text-sm">Create your first campaign to announce deals, updates, or special menus to all your registered customers.</p>
                            <button onClick={() => setView('create')} className="btn-primary px-6 py-3 rounded-xl text-xs uppercase font-black tracking-wider">
                                Create Campaign
                            </button>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-slate-50 border-b border-slate-100 text-xs font-black text-slate-400 uppercase tracking-wider">
                                        <th className="p-5">Campaign / Subject</th>
                                        <th className="p-5">Send Mode</th>
                                        <th className="p-5">Status</th>
                                        <th className="p-5 text-center">Progress</th>
                                        <th className="p-5 text-center">Opened</th>
                                        <th className="p-5 text-center">Bounced</th>
                                        <th className="p-5 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 text-sm text-slate-600">
                                    {campaigns.map((camp) => {
                                        const total = camp.total_recipients || 0;
                                        const sent = camp.sent_count || 0;
                                        const progressPercent = total > 0 ? Math.round((sent / total) * 100) : 0;
                                        const openRate = total > 0 ? Math.round(((camp.opened_count || 0) / total) * 100) : 0;

                                        return (
                                            <tr key={camp.id} className="hover:bg-slate-50/50 transition-colors">
                                                <td className="p-5">
                                                    <div className="font-bold text-slate-900">{camp.name}</div>
                                                    <div className="text-xs text-slate-400 mt-0.5 truncate max-w-xs">{camp.subject}</div>
                                                </td>
                                                <td className="p-5 uppercase text-xs font-bold text-slate-500">
                                                    {camp.send_type === 'batch' ? `Batch (${camp.batch_size} / ${camp.batch_delay_seconds}s)` : 'Normal'}
                                                </td>
                                                <td className="p-5">
                                                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${
                                                        camp.status === 'completed' ? 'bg-green-50 text-green-700 border border-green-200' :
                                                        camp.status === 'sending' ? 'bg-blue-50 text-blue-700 border border-blue-200 animate-pulse' :
                                                        'bg-slate-50 text-slate-500 border border-slate-200'
                                                    }`}>
                                                        {camp.status === 'sending' && <RefreshCw className="animate-spin w-3 h-3 mr-1.5" />}
                                                        {camp.status === 'completed' && <CheckCircle2 className="w-3 h-3 mr-1.5" />}
                                                        {camp.status === 'draft' && <Clock className="w-3 h-3 mr-1.5" />}
                                                        {camp.status}
                                                    </span>
                                                </td>
                                                <td className="p-5">
                                                    {camp.status === 'draft' ? (
                                                        <div className="text-center text-xs text-slate-400 italic">Not sent yet</div>
                                                    ) : (
                                                        <div className="w-32 mx-auto">
                                                            <div className="flex justify-between text-[10px] text-slate-400 mb-1 font-mono font-bold">
                                                                <span>{sent}/{total}</span>
                                                                <span>{progressPercent}%</span>
                                                            </div>
                                                            <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                                                                <div 
                                                                    className="bg-primary-500 h-full rounded-full transition-all duration-300"
                                                                    style={{ width: `${progressPercent}%` }}
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="p-5 text-center font-bold text-slate-800">
                                                    {camp.status === 'draft' ? '-' : (
                                                        <div>
                                                            <div>{camp.opened_count || 0}</div>
                                                            <div className="text-[10px] text-green-600 font-medium">{openRate}% rate</div>
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="p-5 text-center font-bold text-slate-800">
                                                    {camp.status === 'draft' ? '-' : (
                                                        <span className={camp.bounced_count > 0 ? 'text-red-500' : 'text-slate-500'}>
                                                            {camp.bounced_count || 0}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="p-5 text-right space-x-1.5">
                                                    <button 
                                                         onClick={() => viewCampaignDetails(camp.id)}
                                                         disabled={loading || sending}
                                                         className="p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
                                                         title="View Details & Stats"
                                                     >
                                                         <BarChart2 className="w-4.5 h-4.5" />
                                                     </button>
                                                     {camp.status === 'draft' && (
                                                         <button 
                                                             onClick={() => handleSend(camp.id)}
                                                             disabled={loading || sending}
                                                             className="p-2 text-primary-600 hover:text-primary-800 hover:bg-primary-50 rounded-lg transition-colors disabled:opacity-50"
                                                             title="Send Campaign Now"
                                                         >
                                                             {sending ? (
                                                                 <RefreshCw className="animate-spin w-4.5 h-4.5" />
                                                             ) : (
                                                                 <Send className="w-4.5 h-4.5" />
                                                             )}
                                                         </button>
                                                     )}
                                                 </td>
                                             </tr>
                                         );
                                     })}
                                 </tbody>
                             </table>
                         </div>
                     )}
                 </div>
             )}

             {/* Create Campaign View */}
             {view === 'create' && (
                 <div className="grid lg:grid-cols-2 gap-8">
                     {/* Form Input */}
                     <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm space-y-6">
                         <h3 className="text-xl font-bold text-gray-900 border-b border-slate-100 pb-4 flex items-center">
                             <Sparkles className="w-5 h-5 text-primary-500 mr-2" /> Composer
                         </h3>
                         <form onSubmit={handleCreate} className="space-y-6">
                             <div className="space-y-2">
                                 <label className="text-xs font-black text-gray-400 uppercase tracking-widest">Campaign Reference Name</label>
                                 <input 
                                     required 
                                     className="w-full bg-slate-50 border-none rounded-2xl p-4 focus:ring-2 focus:ring-primary-500" 
                                     placeholder="e.g. July Weekend Special Deals Offer" 
                                     value={name} 
                                     onChange={e => setName(e.target.value)} 
                                 />
                             </div>

                             <div className="space-y-2">
                                 <label className="text-xs font-black text-gray-400 uppercase tracking-widest">Email Subject Line</label>
                                 <input 
                                     required 
                                     className="w-full bg-slate-50 border-none rounded-2xl p-4 focus:ring-2 focus:ring-primary-500" 
                                     placeholder="e.g. Get 20% off on all pizzas this Sunday!" 
                                     value={subject} 
                                     onChange={e => setSubject(e.target.value)} 
                                 />
                             </div>

                             <div className="space-y-2">
                                 <div className="flex justify-between items-center">
                                     <label className="text-xs font-black text-gray-400 uppercase tracking-widest">Email Body</label>
                                     <div className="flex space-x-1.5 bg-slate-100 p-1 rounded-xl">
                                         <button 
                                             type="button" 
                                             onClick={() => setContentType('text')} 
                                             className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${contentType === 'text' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-400'}`}
                                         >
                                             Plain Text
                                         </button>
                                         <button 
                                             type="button" 
                                             onClick={() => setContentType('html')} 
                                             className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${contentType === 'html' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-400'}`}
                                         >
                                             HTML Markup
                                         </button>
                                     </div>
                                 </div>
                                 <textarea 
                                     required 
                                     rows="10" 
                                     className="w-full bg-slate-50 border-none rounded-2xl p-4 focus:ring-2 focus:ring-primary-500 font-mono text-sm" 
                                     placeholder={contentType === 'html' ? "<h1>Special Offer!</h1><p>Delicious discounts inside...</p>" : "Hey there,\n\nCheck out our special Sunday discounts..."}
                                     value={body} 
                                     onChange={e => setBody(e.target.value)} 
                                 />
                             </div>

                             {/* Sending Configuration */}
                             <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100 space-y-4">
                                 <h4 className="text-sm font-black text-slate-700 uppercase tracking-wider flex items-center"><Sliders className="w-4 h-4 mr-2" /> Dispatch Settings</h4>
                                 
                                 <div className="flex items-center space-x-6">
                                     <label className="flex items-center space-x-2 cursor-pointer">
                                         <input 
                                             type="radio" 
                                             name="sendType" 
                                             className="text-primary-600 focus:ring-primary-500" 
                                             checked={sendType === 'normal'} 
                                             onChange={() => setSendType('normal')} 
                                         />
                                         <span className="text-sm font-bold text-slate-700">Normal (All at once)</span>
                                     </label>
                                     <label className="flex items-center space-x-2 cursor-pointer">
                                         <input 
                                             type="radio" 
                                             name="sendType" 
                                             className="text-primary-600 focus:ring-primary-500" 
                                             checked={sendType === 'batch'} 
                                             onChange={() => setSendType('batch')} 
                                         />
                                         <span className="text-sm font-bold text-slate-700">Batch Wise Delivery</span>
                                     </label>
                                 </div>

                                 {sendType === 'batch' && (
                                     <div className="grid grid-cols-2 gap-4 pt-2">
                                         <div className="space-y-1">
                                             <label className="text-[10px] uppercase font-black text-gray-400">Batch Size</label>
                                             <input 
                                                 type="number" 
                                                 min="1" 
                                                 className="w-full bg-white border border-slate-200 rounded-xl p-2.5 text-sm focus:ring-2 focus:ring-primary-500" 
                                                 value={batchSize} 
                                                 onChange={e => setBatchSize(e.target.value)} 
                                             />
                                         </div>
                                         <div className="space-y-1">
                                             <label className="text-[10px] uppercase font-black text-gray-400">Delay (Seconds)</label>
                                             <input 
                                                 type="number" 
                                                 min="1" 
                                                 className="w-full bg-white border border-slate-200 rounded-xl p-2.5 text-sm focus:ring-2 focus:ring-primary-500" 
                                                 value={batchDelaySeconds} 
                                                 onChange={e => setBatchDelaySeconds(e.target.value)} 
                                             />
                                         </div>
                                     </div>
                                 )}
                             </div>

                             <button 
                                 type="submit" 
                                 disabled={creating}
                                 className="w-full btn-primary py-4 text-xs font-black uppercase tracking-widest shadow-2xl shadow-primary-200 flex items-center justify-center gap-2 disabled:opacity-70"
                             >
                                 {creating ? (
                                     <>
                                         <RefreshCw className="animate-spin w-4 h-4" />
                                         Saving...
                                     </>
                                 ) : (
                                     'Save Campaign Draft'
                                 )}
                             </button>
                        </form>
                    </div>

                    {/* Live Preview Pane */}
                    <div className="space-y-6">
                        <h3 className="text-xl font-bold text-gray-900 flex items-center">
                            <Eye className="w-5 h-5 text-primary-500 mr-2" /> Live Render Preview
                        </h3>
                        {renderPreview()}
                    </div>
                </div>
            )}

            {/* Campaign Details & Stats View */}
            {view === 'details' && selectedCampaign && (
                <div className="space-y-8 animate-fadeIn">
                    {/* Overview Header */}
                    <div className="bg-slate-900 rounded-3xl p-8 text-white shadow-xl flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                        <div>
                            <span className="text-xs uppercase font-black tracking-widest text-primary-400">Campaign Details</span>
                            <h2 className="text-3xl font-black mt-1">{selectedCampaign.campaign.name}</h2>
                            <p className="text-slate-400 mt-2 text-sm max-w-xl">
                                <strong>Subject:</strong> {selectedCampaign.campaign.subject}
                            </p>
                        </div>
                        <div className="flex flex-col items-end gap-1.5 font-mono text-xs text-slate-400">
                            <span>Status: <strong className="text-white capitalize">{selectedCampaign.campaign.status}</strong></span>
                            <span>Created: <strong>{new Date(selectedCampaign.campaign.created_at).toLocaleString()}</strong></span>
                            {selectedCampaign.campaign.sent_at && (
                                <span>Sent: <strong>{new Date(selectedCampaign.campaign.sent_at).toLocaleString()}</strong></span>
                            )}
                        </div>
                    </div>

                    {/* Quick Metric Cards */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                        <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm flex items-center space-x-4">
                            <div className="p-4 bg-slate-50 text-slate-600 rounded-2xl">
                                <Mail className="w-6 h-6" />
                            </div>
                            <div>
                                <div className="text-2xl font-black text-slate-900">{selectedCampaign.campaign.total_recipients}</div>
                                <div className="text-xs text-slate-400 uppercase font-black tracking-wider mt-0.5">Total Mails</div>
                            </div>
                        </div>

                        <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm flex items-center space-x-4">
                            <div className="p-4 bg-green-50 text-green-600 rounded-2xl">
                                <CheckCircle2 className="w-6 h-6" />
                            </div>
                            <div>
                                <div className="text-2xl font-black text-slate-900">{selectedCampaign.campaign.delivered_count}</div>
                                <div className="text-xs text-slate-400 uppercase font-black tracking-wider mt-0.5">Delivered</div>
                            </div>
                        </div>

                        <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm flex items-center space-x-4">
                            <div className="p-4 bg-blue-50 text-blue-600 rounded-2xl">
                                <Eye className="w-6 h-6" />
                            </div>
                            <div>
                                <div className="text-2xl font-black text-slate-900">{selectedCampaign.campaign.opened_count}</div>
                                <div className="text-xs text-slate-400 uppercase font-black tracking-wider mt-0.5">Opened</div>
                            </div>
                        </div>

                        <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm flex items-center space-x-4">
                            <div className="p-4 bg-red-50 text-red-600 rounded-2xl">
                                <XCircle className="w-6 h-6" />
                            </div>
                            <div>
                                <div className="text-2xl font-black text-slate-900">{selectedCampaign.campaign.bounced_count}</div>
                                <div className="text-xs text-slate-400 uppercase font-black tracking-wider mt-0.5">Bounced</div>
                            </div>
                        </div>
                    </div>

                    {/* Progress Bar (if in sending state) */}
                    {selectedCampaign.campaign.status === 'sending' && (
                        <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm space-y-3">
                            <div className="flex justify-between items-center text-sm">
                                <span className="font-bold text-slate-800 flex items-center">
                                    <RefreshCw className="animate-spin w-4 h-4 mr-2 text-primary-500" />
                                    Sending in progress...
                                </span>
                                <span className="font-mono font-bold text-primary-600">
                                    {Math.round((selectedCampaign.campaign.sent_count / selectedCampaign.campaign.total_recipients) * 100)}%
                                </span>
                            </div>
                            <div className="w-full bg-slate-100 h-3 rounded-full overflow-hidden">
                                <div 
                                    className="bg-primary-500 h-full rounded-full transition-all duration-300"
                                    style={{ width: `${(selectedCampaign.campaign.sent_count / selectedCampaign.campaign.total_recipients) * 100}%` }}
                                />
                            </div>
                        </div>
                    )}

                    {/* Recipient Details & Bounce Reasons */}
                    <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
                        <div className="p-6 border-b border-slate-100 flex justify-between items-center">
                            <h3 className="text-lg font-black text-slate-900">Recipient Dispatch Log</h3>
                            <div className="flex items-center gap-3">
                                <button 
                                    onClick={handleExportCSV}
                                    className="text-xs text-primary-600 hover:text-primary-800 font-bold flex items-center gap-1.5 p-1 rounded hover:bg-slate-50 transition-colors"
                                    title="Export to CSV"
                                >
                                    <Download className="w-4 h-4" /> Export CSV
                                </button>
                                <span className="text-slate-300">|</span>
                                <button 
                                    onClick={() => viewCampaignDetails(selectedCampaign.campaign.id)}
                                    className="text-xs text-slate-500 hover:text-slate-800 font-bold flex items-center gap-1.5 p-1 rounded hover:bg-slate-50 transition-colors"
                                >
                                    <RefreshCw className="w-3.5 h-3.5" /> Refresh Log
                                </button>
                            </div>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-slate-50 border-b border-slate-100 text-xs font-black text-slate-400 uppercase tracking-wider">
                                        <th className="p-5">Recipient Email</th>
                                        <th className="p-5">Status</th>
                                        <th className="p-5">Dispatched At</th>
                                        <th className="p-5">Delivery Details</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 text-sm text-slate-600">
                                    {selectedCampaign.recipients.map((rec) => (
                                        <tr key={rec.id} className="hover:bg-slate-50/50 transition-colors">
                                            <td className="p-5 font-bold text-slate-900">{rec.email}</td>
                                            <td className="p-5">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${
                                                    rec.status === 'opened' ? 'bg-blue-50 text-blue-700 border border-blue-100' :
                                                    rec.status === 'delivered' ? 'bg-green-50 text-green-700 border border-green-100' :
                                                    rec.status === 'bounced' ? 'bg-red-50 text-red-700 border border-red-100' :
                                                    'bg-slate-50 text-slate-500 border border-slate-200'
                                                }`}>
                                                    {rec.status}
                                                </span>
                                            </td>
                                            <td className="p-5 font-mono text-xs text-slate-400">
                                                {rec.sent_at ? new Date(rec.sent_at).toLocaleString() : '-'}
                                            </td>
                                            <td className="p-5 text-xs">
                                                {rec.status === 'bounced' ? (
                                                    <span className="text-red-500 flex items-center gap-1">
                                                        <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                                                        Reason: {rec.bounce_reason || 'Unknown block'}
                                                    </span>
                                                ) : rec.status === 'opened' && rec.opened_at ? (
                                                    <span className="text-blue-500 font-medium">
                                                        Opened email on {new Date(rec.opened_at).toLocaleString()}
                                                    </span>
                                                ) : rec.status === 'delivered' ? (
                                                    <span className="text-green-600">Delivered successfully</span>
                                                ) : (
                                                    <span className="text-slate-400 italic">Waiting...</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
